import { useMemo, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { VERTICALS } from "@/lib/data";
import { ArrowRight, Check, MapPin, Calendar, Phone, ExternalLink, Fuel, Zap, Wrench, Users, GraduationCap } from "lucide-react";

function KpiRow({ kpis }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {kpis.map(k => (
        <div key={k.k} className="border-l-2 border-shramik-green pl-3">
          <div className="font-display text-2xl md:text-3xl font-light text-shramik-ink leading-none">{k.v}</div>
          <div className="mt-1 text-[11px] uppercase tracking-widest text-slate-500">{k.k}</div>
        </div>
      ))}
    </div>
  );
}

function VerticalHero({ v }) {
  return (
    <section className="pt-[120px] md:pt-[160px] pb-16 bg-shramik-greenLight/30 relative overflow-hidden" data-testid={`vertical-hero-${v.slug}`}>
      <div className="orb bg-shramik-green/25 w-[520px] h-[520px] -top-40 -right-20" />
      <div className="container-page relative grid lg:grid-cols-12 gap-12 items-center">
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="lg:col-span-7">
          <div className="overline mb-5">{v.tag}</div>
          <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-balance">
            {v.name}
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed">{v.short}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            {v.highlights.slice(0, 5).map(h => (
              <span key={h} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700">{h}</span>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} transition={{duration:0.7, delay:0.15}} className="lg:col-span-5">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
            <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
            <div className={`absolute inset-0 bg-gradient-to-t ${v.color}`} />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AboutBlock({ v }) {
  return (
    <section className="py-20 md:py-24" data-testid={`vertical-about-${v.slug}`}>
      <div className="container-page grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="overline mb-4">About</div>
          <h2 className="font-display font-light text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
            Our story, <em className="not-italic font-serif italic text-shramik-green">in detail.</em>
          </h2>
          <div className="mt-8 space-y-3 text-sm">
            {v.established && (
              <div className="flex gap-3 items-start">
                <Calendar className="w-4 h-4 text-shramik-green mt-1 shrink-0"/>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Established</div><div className="font-medium">{v.established}</div></div>
              </div>
            )}
            {v.address && (
              <div className="flex gap-3 items-start">
                <MapPin className="w-4 h-4 text-shramik-green mt-1 shrink-0"/>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Address</div><div className="font-medium leading-snug">{v.address}</div></div>
              </div>
            )}
            {v.contact && (
              <div className="flex gap-3 items-start">
                <Phone className="w-4 h-4 text-shramik-green mt-1 shrink-0"/>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Contact</div><div className="font-medium">{v.contact}</div></div>
              </div>
            )}
            {v.website && (
              <a href={v.website} target="_blank" rel="noreferrer" className="flex gap-3 items-start hover:text-shramik-green">
                <ExternalLink className="w-4 h-4 text-shramik-green mt-1 shrink-0"/>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Website</div><div className="font-medium underline decoration-shramik-green/30 underline-offset-4">{v.website.replace(/https?:\/\//,'')}</div></div>
              </a>
            )}
          </div>
        </div>
        <div className="lg:col-span-8 space-y-5 text-lg text-slate-600 leading-relaxed">
          <p className="text-xl text-slate-800">{v.body}</p>
          {(v.longBody || []).map((p, i) => <p key={i}>{p}</p>)}
          <div className="pt-6"><KpiRow kpis={v.kpis} /></div>
        </div>
      </div>
    </section>
  );
}

function ProductsGrid({ v, iconAccent = "green" }) {
  if (!v.products) return null;
  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid={`vertical-products-${v.slug}`}>
      <div className="container-page">
        <div className="overline mb-4">Our {v.slug === "construction" ? "Product Range" : "Products"}</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-12 tracking-tight text-balance max-w-3xl">
          {v.slug === "dairy" ? "Farm-fresh, quality-tested every single day." : v.slug === "construction" ? "Materials engineered for durability." : "Everything the farm & family needs."}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {v.products.map((p, i) => (
            <motion.div key={p.name}
              initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4, delay: i * 0.04}}
              className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green hover:shadow-lg transition-all"
            >
              <div className="text-[10px] uppercase tracking-widest text-shramik-green font-semibold mb-3">{p.tag}</div>
              <div className="font-display font-medium text-lg leading-tight">{p.name}</div>
            </motion.div>
          ))}
        </div>
        {v.upcoming && (
          <div className="mt-14 p-8 md:p-10 rounded-3xl bg-gradient-to-br from-shramik-green to-shramik-greenDark text-white">
            <div className="text-xs uppercase tracking-[0.22em] text-shramik-gold mb-3">Upcoming Expansion</div>
            <h3 className="font-display font-light text-2xl md:text-3xl leading-tight max-w-3xl">₹120 Cr capex — building a premium ingredient-supplier facility.</h3>
            {v.upcomingNote && <p className="mt-4 text-white/85 text-base max-w-3xl leading-relaxed">{v.upcomingNote}</p>}
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {v.upcoming.map(u => (
                <div key={u} className="flex items-center gap-3 text-sm bg-white/10 rounded-xl p-4 backdrop-blur">
                  <span className="w-2 h-2 rounded-full bg-shramik-gold shrink-0"/> {u}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function ServicesBlock({ v }) {
  if (!v.services) return null;
  return (
    <section className="py-16 md:py-20" data-testid={`vertical-services-${v.slug}`}>
      <div className="container-page">
        <div className="overline mb-4">Services Offered</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-12 tracking-tight text-balance max-w-3xl">
          Everything under one <em className="not-italic font-serif italic text-shramik-green">trusted roof.</em>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {v.services.map((s, i) => (
            <motion.div key={s}
              initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4, delay: i * 0.04}}
              className="p-5 rounded-2xl border border-slate-100 bg-white flex items-start gap-3">
              <Check className="w-5 h-5 text-shramik-green mt-0.5 shrink-0"/>
              <div className="font-medium text-slate-800">{s}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BranchesBlock({ v }) {
  if (!v.branches) return null;
  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid={`vertical-branches-${v.slug}`}>
      <div className="container-page">
        <div className="overline mb-4">Our Branches</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-4 tracking-tight text-balance max-w-3xl">
          {v.branches.length}+ locations across <em className="not-italic font-serif italic text-shramik-green">Maharashtra.</em>
        </h2>
        <p className="text-slate-600 max-w-2xl mb-10">Serving farmers in Ahilyanagar, Nashik and Pune districts.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {v.branches.map((b, i) => (
            <motion.div key={b.name}
              initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.4, delay: (i%6) * 0.04}}
              className="group p-6 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-shramik-greenLight text-shramik-green flex items-center justify-center shrink-0"><MapPin className="w-5 h-5"/></div>
                <div className="flex-1">
                  <div className="font-display font-medium text-lg leading-tight">{b.name}</div>
                  <div className="text-sm text-slate-600 mt-1 leading-snug">{b.addr}</div>
                  {b.map && <a href={b.map} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-shramik-green hover:underline">View on Map <ExternalLink className="w-3 h-3"/></a>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OutletsBlock({ v }) {
  if (!v.outlets) return null;
  return (
    <section className="py-16 md:py-24" data-testid={`vertical-outlets-${v.slug}`}>
      <div className="container-page">
        <div className="overline mb-4">Our Outlets</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-12 tracking-tight text-balance max-w-3xl">
          Two flagship stations. <em className="not-italic font-serif italic text-shramik-green">Full-service, always.</em>
        </h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {v.outlets.map((o, i) => (
            <motion.div key={o.name}
              initial={{opacity:0, y:24}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay: i * 0.1}}
              className="rounded-3xl overflow-hidden bg-white border border-slate-100 card-hover" data-testid={`outlet-${o.name.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img src={o.image} alt={o.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"/>
              </div>
              <div className="p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-shramik-green font-semibold">{o.brand} • Since {o.established}</div>
                    <h3 className="font-display text-2xl md:text-3xl font-medium mt-1">{o.name}</h3>
                    <div className="mt-2 text-sm text-slate-500 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0"/> {o.address}</div>
                  </div>
                </div>

                <div className="mt-6 grid gap-5">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2"><Fuel className="w-3.5 h-3.5"/> Fuel</div>
                    <div className="flex flex-wrap gap-2">
                      {o.fuel.map(f => <span key={f} className="px-3 py-1 rounded-full bg-shramik-greenLight text-shramik-green text-xs font-medium">{f}</span>)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2"><Zap className="w-3.5 h-3.5"/> Vehicular Facilities</div>
                    <div className="flex flex-wrap gap-2">
                      {o.vehicular.map(f => <span key={f} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{f}</span>)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2"><Wrench className="w-3.5 h-3.5"/> Consumer Facilities</div>
                    <div className="flex flex-wrap gap-2">
                      {o.consumer.map(f => <span key={f} className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">{f}</span>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamBlock({ v }) {
  if (!v.team) return null;
  return (
    <section className="py-16 md:py-24 bg-shramik-ink text-white overflow-hidden relative" data-testid={`vertical-team-${v.slug}`}>
      <div className="orb bg-shramik-green/25 w-[500px] h-[500px] -top-40 -left-40"/>
      <div className="container-page relative">
        <div className="text-xs uppercase tracking-[0.22em] font-semibold text-shramik-gold mb-4 flex items-center gap-2"><Users className="w-4 h-4"/> Leadership Team</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-12 tracking-tight text-balance max-w-3xl">
          The team that keeps the <em className="not-italic font-serif italic text-shramik-gold">pumps running.</em>
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {v.team.map((t, i) => (
            <motion.div key={t.name}
              initial={{opacity:0, y:24}} whileInView={{opacity:1, y:0}} viewport={{once:true}} transition={{duration:0.5, delay: i * 0.08}}
              className={`rounded-2xl overflow-hidden bg-white/5 border border-white/10 ${i%2 === 1 ? "md:mt-10" : ""}`}>
              <div className="aspect-[3/4] overflow-hidden bg-white/5">
                <img src={t.photo} alt={t.name} loading="lazy" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"/>
              </div>
              <div className="p-4">
                <div className="text-xs text-shramik-gold uppercase tracking-widest">{t.role}</div>
                <div className="font-display font-medium text-base leading-tight mt-1">{t.name}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InstitutionsBlock({ v }) {
  if (!v.institutions) return null;
  return (
    <section className="py-16 md:py-24 bg-slate-50" data-testid={`vertical-institutions-${v.slug}`}>
      <div className="container-page">
        <div className="overline mb-4">Institutions</div>
        <h2 className="font-display font-light text-3xl md:text-5xl mb-12 tracking-tight text-balance max-w-3xl">
          {v.institutions.length} institutions, <em className="not-italic font-serif italic text-shramik-green">one mission.</em>
        </h2>
        <div className="relative">
          <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-shramik-green/25"/>
          <div className="space-y-4">
            {v.institutions.map((inst, i) => (
              <motion.div key={inst.name}
                initial={{opacity:0, x:-20}} whileInView={{opacity:1, x:0}} viewport={{once:true, margin:"-60px"}} transition={{duration:0.4, delay: i * 0.05}}
                className="relative pl-14 md:pl-20 group">
                <span className="absolute left-3 md:left-5 top-4 -translate-x-1/2 w-4 h-4 rounded-full bg-shramik-green ring-4 ring-slate-50 group-hover:ring-shramik-greenLight transition-all"/>
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 p-5 rounded-2xl bg-white border border-slate-100 hover:border-shramik-green hover:shadow-md transition-all">
                  <div className="font-display text-2xl md:text-3xl font-light text-shramik-green shrink-0 w-24">{inst.year}</div>
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-5 h-5 text-shramik-green shrink-0"/>
                    <div className="font-display font-medium text-lg md:text-xl leading-tight">{inst.name}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA({ v }) {
  return (
    <section className="py-20 md:py-24" data-testid={`vertical-cta-${v.slug}`}>
      <div className="container-page">
        <div className="relative rounded-[32px] overflow-hidden bg-shramik-ink px-8 md:px-16 py-16 md:py-20">
          <div className="orb bg-shramik-green/40 w-[500px] h-[500px] -bottom-40 -right-20"/>
          <div className="relative max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-shramik-gold mb-5">Partner with us</div>
            <h2 className="font-display font-light text-white text-3xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-balance">
              Ready to work with <em className="not-italic font-serif italic text-shramik-gold">{v.name}?</em>
            </h2>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary bg-shramik-gold text-shramik-ink hover:bg-white">Talk to this business <ArrowRight className="w-4 h-4"/></Link>
              <Link to="/businesses" className="btn-secondary border-white text-white hover:bg-white/10">Explore other verticals</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function VerticalDetail({ v }) {
  return (
    <>
      <VerticalHero v={v} />
      <AboutBlock v={v} />
      {v.slug === "petroleum" && <OutletsBlock v={v} />}
      {v.services && <ServicesBlock v={v} />}
      {v.products && <ProductsGrid v={v} />}
      {v.branches && <BranchesBlock v={v} />}
      {v.institutions && <InstitutionsBlock v={v} />}
      {v.team && <TeamBlock v={v} />}
      <CTA v={v} />
    </>
  );
}

export default function Verticals() {
  const { slug } = useParams();
  const active = useMemo(() => VERTICALS.find(v => v.slug === slug), [slug]);
  const detailRef = useRef(null);

  useEffect(() => {
    if (slug && detailRef.current) {
      setTimeout(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [slug]);

  return (
    <div data-testid="verticals-page">
      {!active && (
        <section className="pt-[120px] md:pt-[160px] pb-16 bg-shramik-greenLight/40 relative overflow-hidden">
          <div className="orb bg-shramik-green/20 w-[500px] h-[500px] -top-40 -right-20"/>
          <div className="container-page relative">
            <div className="overline mb-5">Business Verticals</div>
            <h1 className="font-display font-light text-5xl md:text-7xl leading-[1.02] tracking-tighter max-w-4xl text-balance">
              Five verticals, each a <em className="not-italic font-serif italic text-shramik-green">force multiplier</em> for the next.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl">From soil to shelf, from concrete to community — Shramik Group's businesses are engineered to reinforce one another.</p>
          </div>
        </section>
      )}

      {/* Tabs */}
      <section className={`sticky top-[72px] md:top-[84px] z-30 bg-white/85 backdrop-blur-xl border-y border-slate-200`} data-testid="verticals-tabs">
        <div className="container-page overflow-x-auto no-scrollbar">
          <div className="flex gap-2 py-4 min-w-max">
            <Link to="/businesses" data-testid="vertical-tab-all" className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${!slug ? "bg-shramik-green text-white" : "bg-slate-100 hover:bg-shramik-greenLight text-slate-700"}`}>Overview</Link>
            {VERTICALS.map(v => (
              <Link key={v.slug} to={`/businesses/${v.slug}`} data-testid={`vertical-tab-${v.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${slug === v.slug ? "bg-shramik-green text-white" : "bg-slate-100 hover:bg-shramik-greenLight text-slate-700"}`}>
                {v.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <div ref={detailRef} />

      {active ? (
        <VerticalDetail v={active} />
      ) : (
        // Overview: brief section for each vertical
        VERTICALS.map((v, i) => (
          <section key={v.slug} className={`py-20 md:py-24 ${i % 2 === 1 ? "bg-slate-50" : ""}`} data-testid={`vertical-section-${v.slug}`}>
            <div className="container-page grid lg:grid-cols-12 gap-12 items-center">
              <motion.div initial={{opacity:0,x:-20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.6}} className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
                  <img src={v.image} alt={v.name} className="w-full h-full object-cover"/>
                  <div className={`absolute inset-0 bg-gradient-to-t ${v.color} mix-blend-multiply`}/>
                </div>
              </motion.div>
              <motion.div initial={{opacity:0,x:20}} whileInView={{opacity:1,x:0}} viewport={{once:true}} transition={{duration:0.6}} className="lg:col-span-6">
                <div className="overline mb-4">{v.tag}</div>
                <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">{v.name}</h2>
                <p className="mt-6 text-lg text-slate-600 leading-relaxed">{v.body}</p>
                <div className="mt-8 grid grid-cols-2 gap-4">
                  {v.highlights.slice(0,6).map(h => (
                    <div key={h} className="flex items-center gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-shramik-green"/> {h}</div>
                  ))}
                </div>
                <div className="mt-10"><KpiRow kpis={v.kpis} /></div>
                <Link to={`/businesses/${v.slug}`} className="mt-10 btn-primary inline-flex" data-testid={`vertical-cta-${v.slug}`}>Explore in detail <ArrowRight className="w-4 h-4"/></Link>
              </motion.div>
            </div>
          </section>
        ))
      )}
    </div>
  );
}
